// @flow
import React, { Component } from "react";
import Page from "../../components/Page";
import Async from "../../components/Async";
import api from "../../services/api";
import styled from "styled-components";
import { Button, DeleteButton } from "../../components/buttons";
import CreateDialog from "./CreateDialog";
import Policies from "./Policies";
import DeliveryStatus, { StatusBadge } from "./DeliveryStatus";
import List from "../../components/List";
import toasts from "../../services/toasts";
import { borderColor, spacing } from "../../components/layout";

const PushNotification = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(160px, auto) minmax(130px, auto) auto;
  gap: 14px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing};
  margin-bottom: 20px;
`;

const ToolbarText = styled.div`
  max-width: 760px;
  line-height: 1.45;
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Muted = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: ${spacing};
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SummaryLabel = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-top: 6px;
`;

const SummaryMeta = styled.div`
  color: var(--muted-text);
  font-size: 12px;
  margin-top: 6px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${spacing};
  margin-bottom: 26px;
`;

const SectionCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
`;

const SectionHint = styled.div`
  color: var(--muted-text);
  font-size: 12px;
  margin-bottom: 14px;
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 72px;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const BreakdownLabel = styled.div`
  min-width: 0;
`;

const BreakdownLabelText = styled.div`
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BreakdownBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BreakdownFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: #54d2b1;
  border-radius: 999px;
`;

const BreakdownCount = styled.div`
  text-align: right;
  font-weight: 600;
`;

const ScheduledSection = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  background: var(--surface-bg);
  overflow: hidden;
`;

const ScheduledHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${borderColor};
`;

const PushMessage = styled.div`
  min-width: 0;
`;

const PushMessageText = styled.div`
  font-weight: 600;
`;

const PushMessageMeta = styled.div`
  color: var(--muted-text);
  font-size: 12px;
  margin-top: 4px;
`;

const EmptyState = styled.div`
  color: var(--muted-text);
  font-size: 13px;
  padding: 16px;
`;

type P = {};
type S = {
  showCreateDialog: boolean,
  editingPush: any
};

class PushNotifications extends Component<P, S> {
  state = {
    showCreateDialog: false,
    editingPush: null
  };

  policies = React.createRef<Policies>();
  stats = React.createRef<Async>();
  listing = React.createRef<Async>();

  refreshData = () => {
    if (this.policies.current) this.policies.current.refresh();
    if (this.stats.current) {
      this.stats.current.refresh();
    }
    if (this.listing.current) {
      this.listing.current.refresh();
    }
  };

  refreshScheduled = () => {
    if (this.listing.current) this.listing.current.refresh();
  };

  formatPercent = (count: number, total: number) => {
    if (!total) return "0%";
    return `${Math.round((count / total) * 100)}%`;
  };

  renderBreakdownCard = (title: string, items: Array<any>, total: number, hint: string) => (
    <SectionCard>
      <SectionTitle>{title}</SectionTitle>
      <SectionHint>{hint}</SectionHint>
      {items && items.length > 0 ? items.map(item => {
        const width = total > 0 ? Math.max((item.count / total) * 100, 2) : 0;
        return (
          <BreakdownRow key={item.key}>
            <BreakdownLabel>
              <BreakdownLabelText>{item.label}</BreakdownLabelText>
              <BreakdownBar>
                <BreakdownFill width={width} />
              </BreakdownBar>
            </BreakdownLabel>
            <BreakdownCount>{item.count}</BreakdownCount>
          </BreakdownRow>
        );
      }) : <Muted>No data yet.</Muted>}
    </SectionCard>
  );

  renderStats = (stats: any) => {
    const summary = stats.summary || {};
    const breakdowns = stats.breakdowns || {};
    const syncedUsers = summary.synced_users || 0;
    const totalUsers = summary.total_users || 0;

    const cards = [
      {
        label: "Total users",
        value: summary.total_users || 0,
        meta: `${summary.approved_users || 0} approved`
      },
      {
        label: "Legacy reachable",
        value: summary.legacy_reachable_users || 0,
        meta: `${this.formatPercent(summary.legacy_reachable_users || 0, totalUsers)} of total`
      },
      {
        label: "Legacy approved",
        value: summary.legacy_reachable_approved_users || 0,
        meta: `${this.formatPercent(summary.legacy_reachable_approved_users || 0, summary.legacy_reachable_users || 0)} of legacy`
      },
      {
        label: "APNs token on file",
        value: summary.users_with_device_token || 0,
        meta: `${this.formatPercent(summary.users_with_device_token || 0, totalUsers)} of total`
      },
      {
        label: "Permission synced",
        value: syncedUsers,
        meta: `${this.formatPercent(syncedUsers, totalUsers)} of total`
      },
      {
        label: "Notifications ON",
        value: summary.notifications_enabled_users || 0,
        meta: `${this.formatPercent(summary.notifications_enabled_users || 0, syncedUsers)} of synced`
      },
      {
        label: "Alerts ON",
        value: summary.alerts_enabled_users || 0,
        meta: `${this.formatPercent(summary.alerts_enabled_users || 0, syncedUsers)} of synced`
      },
      {
        label: "Denied",
        value: summary.denied_users || 0,
        meta: `${this.formatPercent(summary.denied_users || 0, syncedUsers)} of synced`
      },
      {
        label: "Never synced",
        value: summary.never_synced_users || 0,
        meta: `${this.formatPercent(summary.never_synced_users || 0, totalUsers)} of total`
      },
      {
        label: "Synced last 7d",
        value: summary.synced_last_7d_users || 0,
        meta: `${this.formatPercent(summary.synced_last_7d_users || 0, totalUsers)} of total`
      },
      {
        label: "Legacy + APNs",
        value: summary.legacy_and_apns_users || 0,
        meta: `${this.formatPercent(summary.legacy_and_apns_users || 0, summary.legacy_reachable_users || 0)} of legacy`
      }
    ];

    return (
      <React.Fragment>
        <Muted>
          iOS permission data is synced when the app logs in or returns to foreground, at most once every 24 hours.
          {" "}Legacy reachable counts come from older Expo/Firebase token registrations and may overstate who is still truly reachable today.
          {stats.generated_at ? ` Last refreshed ${new Date(stats.generated_at).toLocaleString()}.` : ""}
        </Muted>
        <SummaryGrid>
          {cards.map(card => (
            <SummaryCard key={card.label}>
              <SummaryLabel>{card.label}</SummaryLabel>
              <SummaryValue>{card.value}</SummaryValue>
              <SummaryMeta>{card.meta}</SummaryMeta>
            </SummaryCard>
          ))}
        </SummaryGrid>
        <SectionGrid>
          {this.renderBreakdownCard(
            "Authorization Status",
            breakdowns.authorization_status || [],
            syncedUsers,
            "Among users who have synced iOS notification settings."
          )}
          {this.renderBreakdownCard(
            "Alert Setting",
            breakdowns.alert_setting || [],
            syncedUsers,
            "Whether iOS alerts are enabled for synced users."
          )}
          {this.renderBreakdownCard(
            "Legacy Provider",
            breakdowns.legacy_provider || [],
            summary.legacy_reachable_users || 0,
            "Older push-registration rows from the legacy Expo/Firebase token table."
          )}
          {this.renderBreakdownCard(
            "Sync Freshness",
            breakdowns.sync_freshness || [],
            totalUsers,
            "How recently the app reported notification settings back to the backend."
          )}
        </SectionGrid>
      </React.Fragment>
    );
  };

  createPushNotification = async (p: any) => {
    try {
      const values = { ...p, datetime: new Date(p.datetime).toISOString() };
      if (this.state.editingPush) {
        await api.push_notifications.update(this.state.editingPush.id, values);
      } else {
        await api.push_notifications.create(values);
      }
      this.refreshData();
      this.setState({ showCreateDialog: false, editingPush: null });
    } catch (err) {
      const message = (err.response && err.response.data && err.response.data.message) || err.toString();
      toasts.error(message);
    }
  };

  deletePushNotification = async (push_id: any) => {
    try {
      await api.push_notifications.delete({ push_id });
      this.refreshData();
    } catch (err) {
      toasts.error((err.response && err.response.data && err.response.data.message) || err.toString());
    }
  };

  render() {
    return (
      <Page title="Push notifications">
        <Toolbar>
          <ToolbarText>
            <Muted>
              Manage automatic notification types and triggers, scheduled messages, and iOS permission stats.
            </Muted>
          </ToolbarText>
          <ToolbarActions>
            <Button onClick={this.refreshData}>
              Refresh
            </Button>
            <Button onClick={() => this.setState({ showCreateDialog: true, editingPush: null })}>
              Create push notification
            </Button>
          </ToolbarActions>
        </Toolbar>

        <Policies ref={this.policies} onChange={this.refreshScheduled} />

        <Async fetchProps={api.push_notifications.stats} ref={this.stats}>
          {this.renderStats}
        </Async>

        <ScheduledSection>
          <ScheduledHeader>
            <SectionTitle>Scheduled pushes</SectionTitle>
            <SectionHint>
              Admin messages currently target approved test user 420. A Place ID restricts delivery to users within 3 km of that place.
            </SectionHint>
          </ScheduledHeader>
          <Async fetchProps={api.push_notifications.list} ref={this.listing}>
            {push_notifications => (
              push_notifications && push_notifications.length > 0 ? (
                <List>
                  {push_notifications.map(({ message, datetime, id, place_id, delivery }) => {
                    const isPast = datetime ? (new Date(datetime)).getTime() < Date.now() : false;

                    return (
                      <PushNotification key={`row-${id}`}>
                        <PushMessage>
                          <PushMessageText>{message}</PushMessageText>
                          <PushMessageMeta>
                            {isPast ? "Send time passed" : "Scheduled"} · <StatusBadge status={delivery ? delivery.status : "unknown"} />
                          </PushMessageMeta>
                          <DeliveryStatus delivery={delivery} />
                        </PushMessage>
                        <span>{datetime ? (new Date(datetime)).toLocaleString() : null}</span>
                        <span>{place_id !== null ? `Place ID: ${place_id}` : "No Place ID"}</span>
                        <ToolbarActions>
                          <Button onClick={() => this.setState({ showCreateDialog: true, editingPush: { id, datetime, message, place_id } })}>Edit</Button>
                          <DeleteButton
                            confirm={`Delete push notification - ${message}`}
                            onClick={() => this.deletePushNotification(id)}
                          />
                        </ToolbarActions>
                      </PushNotification>
                    );
                  })}
                </List>
              ) : (
                <EmptyState>No scheduled push notifications yet.</EmptyState>
              )
            )}
          </Async>
        </ScheduledSection>
        {this.state.showCreateDialog && (
          <CreateDialog
            initialValues={this.state.editingPush}
            onClose={() => this.setState({ showCreateDialog: false, editingPush: null })}
            onSubmit={this.createPushNotification}
          />
        )}
      </Page>
    );
  }
}

export default PushNotifications;
